/**
 * フォント設定コンポーネント
 * 
 * ユーザーがサイト全体のフォントを選択するためのUI。
 * 日本語/英語のタブ切り替え、検索、プレビュー機能を提供。
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useFont, SYSTEM_FONT, type GoogleFont } from '../contexts/FontContext';
import { loadGoogleFont } from '../api/googleFonts';
import { useLanguage } from '../hooks/useLanguage';
import './FontSettings.css';

/**
 * フォント設定コンポーネント
 */
const FontSettings: React.FC = () => {
  const { japaneseFonts, latinFonts, selectedFont, setFont, isLoading } = useFont();
  const lang = useLanguage();
  // 言語に応じてタブの初期選択を決定
  const [activeTab, setActiveTab] = useState<'japanese' | 'latin'>(lang === 'ja' ? 'japanese' : 'latin');
  const [searchQuery, setSearchQuery] = useState('');

  // 現在のタブのフォント一覧
  const currentFonts = activeTab === 'japanese' ? japaneseFonts : latinFonts;

  // 検索フィルタリング
  const filteredFonts = currentFonts.filter(font =>
    font.family.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 人気フォント（最初の5件）とその他を分離
  const presetCount = 5;
  const popularFonts = filteredFonts.slice(0, presetCount);
  const otherFonts = filteredFonts.slice(presetCount);

  // システムフォントを選択
  const handleSystemFontClick = useCallback(() => {
    setFont(SYSTEM_FONT);
  }, [setFont]);

  // フォントを選択
  const handleFontClick = useCallback((font: GoogleFont) => {
    setFont(font);
  }, [setFont]);

  return (
    <div className="font-settings">
      {/* セクションタイトル */}
      <div className="font-settings__header">
        <h4 className="font-settings__title">
          {lang === 'ja' ? 'フォント' : 'Font'}
        </h4>
        <div className="font-settings__info">
          <span className="font-settings__info-icon" aria-label="info">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
          <div className="font-settings__tooltip">
            <p>
              {lang === 'ja' 
                ? 'Google Fontsを使用しています。' 
                : 'Using Google Fonts.'}
            </p>
            <p>
              {lang === 'ja'
                ? 'フォント名は以下で検索できます：'
                : 'Search for fonts at:'}
            </p>
            <a 
              href="https://fonts.google.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-settings__tooltip-link"
            >
              fonts.google.com
            </a>
          </div>
        </div>
      </div>

      {/* 選択中のフォント表示 */}
      <div className="font-settings__current">
        <span className="font-settings__current-label">
          {lang === 'ja' ? '選択中：' : 'Selected:'}
        </span>
        <span 
          className="font-settings__current-font"
          style={selectedFont.isSystem ? {} : { fontFamily: `"${selectedFont.family}", ${selectedFont.category}` }}
        >
          {selectedFont.isSystem 
            ? (lang === 'ja' ? 'システムフォント' : 'System Font')
            : selectedFont.family}
        </span>
        {!selectedFont.isSystem && (
          <button
            className="font-settings__reset-btn"
            onClick={handleSystemFontClick}
            title={lang === 'ja' ? 'システムフォントに戻す' : 'Reset to System Font'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            {lang === 'ja' ? 'リセット' : 'Reset'}
          </button>
        )}
      </div>

      {/* タブ切り替え */}
      <div className="font-settings__tabs">
        <button
          className={`font-settings__tab ${activeTab === 'japanese' ? 'font-settings__tab--active' : ''}`}
          onClick={() => setActiveTab('japanese')}
        >
          {lang === 'ja' ? '日本語' : 'Japanese'}
        </button>
        <button
          className={`font-settings__tab ${activeTab === 'latin' ? 'font-settings__tab--active' : ''}`}
          onClick={() => setActiveTab('latin')}
        >
          English
        </button>
      </div>

      {/* 人気フォントセクション */}
      <div className="font-settings__section">
        <h5 className="font-settings__section-title">
          {lang === 'ja' ? '人気フォント' : 'Popular'}
        </h5>
        <div className="font-settings__list">
          {popularFonts.map(font => (
            <FontButton
              key={font.family}
              font={font}
              isSelected={selectedFont.family === font.family && !selectedFont.isSystem}
              onClick={() => handleFontClick(font)}
              isJapanese={activeTab === 'japanese'}
            />
          ))}
        </div>
      </div>

      {/* その他のフォントセクション */}
      {(otherFonts.length > 0 || isLoading) && (
        <div className="font-settings__section">
          <h5 className="font-settings__section-title">
            {lang === 'ja' ? 'その他' : 'More Fonts'}
          </h5>
          
          {/* 検索入力 */}
          <input
            type="text"
            className="font-settings__search"
            placeholder={lang === 'ja' ? 'フォント名で検索...' : 'Search fonts...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* フォントリスト */}
          <div className="font-settings__list font-settings__list--scrollable">
            {isLoading ? (
              <div className="font-settings__loading">
                {lang === 'ja' ? '読み込み中...' : 'Loading...'}
              </div>
            ) : otherFonts.length > 0 ? (
              otherFonts.map(font => (
                <FontButton
                  key={font.family}
                  font={font}
                  isSelected={selectedFont.family === font.family && !selectedFont.isSystem}
                  onClick={() => handleFontClick(font)}
                  isJapanese={activeTab === 'japanese'}
                />
              ))
            ) : searchQuery ? (
              <div className="font-settings__no-results">
                {lang === 'ja' ? '該当するフォントがありません' : 'No fonts found'}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * フォントボタンコンポーネント（プレビュー付き）
 * Intersection Observerを使用して、表示された時点でフォントをロード
 */
interface FontButtonProps {
  font: GoogleFont;
  isSelected: boolean;
  onClick: () => void;
  isJapanese: boolean;
}

// サンプルテキスト
const SAMPLE_TEXT_JA = 'あいうえお 永遠に完成しない';
const SAMPLE_TEXT_EN = 'The quick brown fox jumps';

const FontButton: React.FC<FontButtonProps> = ({ font, isSelected, onClick, isJapanese }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const loadedRef = useRef(false);

  // 要素が表示されたらフォントをロード
  useEffect(() => {
    const button = buttonRef.current;
    if (!button || loadedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loadedRef.current) {
            loadGoogleFont(font);
            loadedRef.current = true;
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(button);

    return () => {
      observer.disconnect();
    };
  }, [font]);

  const sampleText = isJapanese ? SAMPLE_TEXT_JA : SAMPLE_TEXT_EN;

  return (
    <button
      ref={buttonRef}
      className={`font-settings__font-btn ${isSelected ? 'font-settings__font-btn--selected' : ''}`}
      style={{ fontFamily: `"${font.family}", ${font.category}` }}
      onClick={onClick}
    >
      <span className="font-settings__font-name">{font.family}</span>
      <span className="font-settings__font-sample">{sampleText}</span>
    </button>
  );
};

export default FontSettings;
